/**
 * Simple Infinite Scroll for Collection Pages - Shopify
 * Loads products in batches of 15 as the user scrolls down the page
 */

class InfiniteScroll {
  constructor() {
    this.isLoading = false;
    this.hasMoreProducts = true;
    this.page = 2; // Start from page 2 (page 1 is initial load)
    this.collectionHandle = this.getCollectionHandle();
    this.loadedProductIds = new Set(); // Track which products we've loaded
    this.consecutiveEmptyResponses = 0; // Track empty responses
    this.init();
  }

  init() {
    // Only initialize on collection pages
    if (!this.collectionHandle) return;

    // Get product grid elements
    this.productGrid = document.getElementById('product-grid');
    this.productGridContainer = document.getElementById('ProductGridContainer');
    
    if (!this.productGrid || !this.productGridContainer) return;
    
    // Collect initial product IDs to avoid duplicates
    const initialProducts = this.productGrid.querySelectorAll('.grid__item');
    initialProducts.forEach(item => {
      const productId = item.getAttribute('data-product-id');
      if (productId) {
        this.loadedProductIds.add(productId);
      }
    });
    console.log(`Initial products loaded: ${initialProducts.length}, unique IDs: ${this.loadedProductIds.size}`);
    
    // Create and append loading indicator
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.classList.add('infinite-scroll-loading');
    this.loadingIndicator.innerHTML = `
      <div class="loading-overlay__spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
    this.productGridContainer.appendChild(this.loadingIndicator);
    this.loadingIndicator.style.display = 'none'; // Initially hidden
    
    // Hide pagination if it exists
    const pagination = document.querySelector('.pagination');
    if (pagination) pagination.style.display = 'none';
    
    // Initialize scroll event for infinite scrolling
    this.setupScrollListener();
    
    // Check once on page load (in case page is short)
    setTimeout(() => this.checkScrollPosition(), 500);
  }
  
  getCollectionHandle() {
    // Extract collection handle from URL
    const path = window.location.pathname;
    const matches = path.match(/\/collections\/([a-zA-Z0-9-_]+)/);
    return matches ? matches[1] : null;
  }
  
  setupScrollListener() {
    // Throttle the scroll event to prevent too many checks
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      if (scrollTimeout) return;
      
      scrollTimeout = setTimeout(() => {
        this.checkScrollPosition();
        scrollTimeout = null;
      }, 100);
    });
  }
  
  checkScrollPosition() {
    if (this.isLoading || !this.hasMoreProducts) return;
    
    // Calculate if we're close to the bottom of the page
    const scrollPosition = window.scrollY + window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // If we're within 400px of the bottom, load more products
    if (documentHeight - scrollPosition < 1200) {
      this.loadMoreProducts();
    }
  }
  
  loadMoreProducts() {
    if (this.isLoading || !this.hasMoreProducts) return;
    
    this.isLoading = true;
    this.loadingIndicator.style.display = 'flex';
    
    console.log('Loading page:', this.page);
    
    // Use current path to preserve filters/tags in AJAX requests
    fetch(`${window.location.pathname}?page=${this.page}&view=ajax`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then(html => {
        // Look for end-of-collection marker in AJAX response
        if (html.includes('end-of-collection-marker')) {
          console.log('End of collection marker found - ending infinite scroll');
          this.hasMoreProducts = false;
          this.loadingIndicator.remove();
          return;
        }
        // Parse and filter only real product cards
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        // Only select grid__item with a valid product ID
        const productItems = Array.from(tempDiv.querySelectorAll('.grid__item')).filter(item => {
          const productId = item.getAttribute('data-product-id');
          return productId && !this.loadedProductIds.has(productId);
        });
        if (productItems.length === 0) {
          console.log('No valid product items found in response');
          this.hasMoreProducts = false;
          this.loadingIndicator.remove();
          return;
        }
        console.log(`Appending ${productItems.length} products in page ${this.page}`);
        productItems.forEach(item => {
          const productId = item.getAttribute('data-product-id');
          this.loadedProductIds.add(productId);
          this.productGrid.appendChild(item);
        });
        // Move to the next page
        this.page++;
        // Reset loading state
        this.isLoading = false;
        // Check if we need to load more
        this.checkScrollPosition();
      })
      .catch(error => {
        console.error('Error loading products:', error);
        this.isLoading = false;
        this.loadingIndicator.style.display = 'none';
      });
  }
}

// Initialize when DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  new InfiniteScroll();
});
